import { defineStore } from 'pinia';
import axios from 'axios';
import type { PaginatedResults, PremiumBundle, CourseBundlesApiResponse } from '~/types/types';
import useAuthStore from './auth';
import { 
    defaultResponseStatus, 
    handleApiRequest
} from '~/functions/handleApiRequest';
import type { ResponseStatus } from '~/lms';

interface BundleStoreState {
    lmsApiBaseurl: string;
    paginatedBundles: PaginatedResults<PremiumBundle> | undefined;
    currentBundle: PremiumBundle | undefined;
    courseBundlesData: CourseBundlesApiResponse | undefined;
    isFetchingBundles: boolean;
    isFetchingBundle: boolean;
    isFetchingCourseBundles: boolean;
    bundlesResponseStatus: ResponseStatus;
    bundleResponseStatus: ResponseStatus;
    courseBundlesResponseStatus: ResponseStatus;
}

const useBundleStore = defineStore('bundles', {
    state: (): BundleStoreState => ({
        lmsApiBaseurl: '',
        paginatedBundles: undefined,
        currentBundle: undefined,
        courseBundlesData: undefined,
        isFetchingBundles: false,
        isFetchingBundle: false,
        isFetchingCourseBundles: false,
        bundlesResponseStatus: defaultResponseStatus,
        bundleResponseStatus: defaultResponseStatus,
        courseBundlesResponseStatus: defaultResponseStatus,
    }),

    getters: {
        bundles: (state) => state.paginatedBundles?.data || [],
        currentPage: (state) => state.paginatedBundles?.meta.current_page || 1,
        totalPages: (state) => state.paginatedBundles?.meta.last_page || 0,
        totalBundles: (state) => state.paginatedBundles?.meta.total || 0,
        hasNextPage: (state) => {
            const meta = state.paginatedBundles?.meta;
            return meta ? meta.current_page < meta.last_page : false;
        },
        hasPrevPage: (state) => {
            const meta = state.paginatedBundles?.meta;
            return meta ? meta.current_page > 1 : false;
        },
        /**
         * Find a bundle from the paginated list by slug - useful for getting purchase status during loading
         */
        getBundleBySlug: (state) => {
            return (slug: string) => {
                return state.paginatedBundles?.data.find(bundle => bundle.page_slug === slug);
            };
        },
    },

    actions: {
        /**
         * @deprecated Use `usePremiumBundles()` composable instead (TanStack Query)
         * @see composables/usePremiumBundles.ts
         */
        async setPremiumBundles(page?: number, courseSlug?: string) {
            if (!useAuthStore().authHeaders) {
                return;
            }

            if (this.isFetchingBundles) {
                return;
            }

            const params = new URLSearchParams();
            if (page !== undefined) {
                params.set('page', page.toString());
            }
            if (courseSlug) {
                params.set('course', courseSlug);
            }
            
            const queryString = params.toString();
            const url = `${this.lmsApiBaseurl}premium-bundles${queryString ? `?${queryString}` : ''}`;

            const result = await handleApiRequest(
                this,
                'isFetchingBundles',
                'bundlesResponseStatus',
                () =>
                    axios.get(url, {
                        headers: useAuthStore().authHeaders,
                    }),
                { ref: 'paginatedBundles', value: undefined },
            );

            if (result) {
                this.paginatedBundles = result;
            }
        },

        /**
         * @deprecated Use `usePremiumBundle()` composable instead (TanStack Query)
         * @see composables/usePremiumBundles.ts
         */
        async setCurrentBundle(bundleSlug: string) {
            if (!useAuthStore().authHeaders) {
                return;
            }

            if (this.isFetchingBundle) {
                return;
            }

            const result = await handleApiRequest(
                this,
                'isFetchingBundle',
                'bundleResponseStatus',
                () =>
                    axios.get(`${this.lmsApiBaseurl}premium-bundles/${bundleSlug}`, {
                        headers: useAuthStore().authHeaders,
                    }),
                { ref: 'currentBundle', value: undefined },
            );

            if (result?.data) {
                this.currentBundle = result.data;
            }
        },

        async getBundleById(bundleId: number): Promise<PremiumBundle | undefined> {
            if (!useAuthStore().authHeaders) {
                return undefined;
            }

            try {
                const response = await axios.get(`${this.lmsApiBaseurl}premium-bundles/${bundleId}`, {
                    headers: useAuthStore().authHeaders,
                });
                return response.data.data;
            } catch (error) {
                console.error('Error fetching bundle by ID:', error);
                return undefined;
            }
        },


        clearCurrentBundle() {
            this.currentBundle = undefined;
            this.bundleResponseStatus = defaultResponseStatus;
        },

        async fetchCourseBundles(courseSlug: string) {
            if (!useAuthStore().authHeaders) {
                return;
            }

            if (this.isFetchingCourseBundles) {
                return;
            }

            const result = await handleApiRequest(
                this,
                'isFetchingCourseBundles',
                'courseBundlesResponseStatus',
                () =>
                    axios.get(`${this.lmsApiBaseurl}courses/${courseSlug}/bundles`, {
                        headers: {
                            ...useAuthStore().authHeaders,
                            'X-Requesting-Portal': 'ipa'
                        },
                    }),
                { ref: 'courseBundlesData', value: undefined },
            );

            if (result) {
                this.courseBundlesData = result;
            }

            return result;
        },

        clearCourseBundles() {
            this.courseBundlesData = undefined;
            this.courseBundlesResponseStatus = defaultResponseStatus;
        },

        clearBundles() {
            this.paginatedBundles = undefined;
            this.bundlesResponseStatus = defaultResponseStatus;
        },
    },
});

export default useBundleStore;